/**
 * The learner's own language, carried from the portal into the course player
 * and applied on a first visit (locale audit 2026-09-26, L-02; the B02 hand-off).
 *
 * The API says each learner's language on sign-in (`recipient.locale`, one of
 * the interface languages), and the interface language is saved per host
 * (`hf-locale` in localStorage and a host-only cookie). Two gaps followed:
 *
 *   - the portal always opened in English, even for an employee the directory
 *     lists as Arabic-first;
 *   - the course player lives on another host (the API's FRONTEND_URL), so a
 *     learner who reads the portal in Arabic opened their course in English.
 *
 * `learnerStartLocale` decides the first; `withPlayerLanguage` /
 * `playerLanguageFrom` carry the language across hosts on the player link.
 */
import { toSupportedLocale, type Locale } from '@/lib/i18n'

/** The query parameter that hands the interface language to the player. */
export const PLAYER_LANGUAGE_PARAM = 'hl'

/**
 * The language to open the portal in for this session, or null to leave the
 * interface as it is.
 *
 *   - A language saved on this device is the learner's own choice, and wins.
 *   - An admin's "View as in Portal" session is the admin's browser: the
 *     employee's language is never applied (it would also be saved as the
 *     admin's choice).
 *   - Only a language the portal has a catalogue for.
 */
export function learnerStartLocale(opts: {
  recipient: { locale?: string | null } | null | undefined
  stored: Locale | null
  isAdminPreview: boolean
}): Locale | null {
  if (opts.stored || opts.isAdminPreview) return null
  return toSupportedLocale(opts.recipient?.locale)
}

/**
 * The player link with the reader's interface language on it (`?hl=ar`), so
 * the player — on another host, with its own saved language — opens in it.
 * Without a language we have, or for a link that cannot be parsed, the link
 * is returned as it was.
 */
export function withPlayerLanguage(playerUrl: string, locale: string | null | undefined): string {
  const language = toSupportedLocale(locale)
  if (!language) return playerUrl
  try {
    const absolute = /^[a-z][a-z\d+.-]*:/i.test(playerUrl)
    const url = new URL(playerUrl, 'https://player.invalid')
    url.searchParams.set(PLAYER_LANGUAGE_PARAM, language)
    return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`
  } catch {
    return playerUrl
  }
}

/**
 * The language to put on the player link: the one saved on this device, else
 * the one on screen when it is not English. A language on screen that is not
 * saved means saving it failed (site data blocked), and without this the
 * player opened in English. English with nothing saved is the default rather
 * than a choice, so the link is left alone and the player keeps its own.
 */
export function playerLanguageToHand(saved: Locale | null, shown: Locale): Locale | null {
  return saved ?? (shown === 'en' ? null : shown)
}

/** The interface language a player link carries, or null (absent or not one we have). */
export function playerLanguageFrom(params: Pick<URLSearchParams, 'get'> | null | undefined): Locale | null {
  return toSupportedLocale(params?.get(PLAYER_LANGUAGE_PARAM))
}

/**
 * How the portal leaves for the player. An object so a test can stand in for
 * the one step jsdom cannot take: it forbids stubbing `location.replace`.
 */
export const playerNavigation = {
  /** Same tab, replacing the bridge page, so Back returns to the training shelf. */
  replace(url: string): void {
    window.location.replace(url)
  },
}
